import React, { useRef, useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import html2canvas from 'html2canvas';

interface CertificateProps {
  memberName: string;
  eventTitle: string;
  eventDate: string;
  points: number;
  memberId: string;
  profileImage?: string;
  certificateTemplate?: string;
}

// Ratios relative to the loaded image's actual pixel dimensions.
// nameY / nameX = center of the name text (0–1).
// fontSize = fraction of image width used as font size in px.
const TEMPLATE_CONFIGS: Record<string, {
  nameY: number;
  nameX: number;
  fontSize: number;
  color: string;
  fontStyle: string;
}> = {
  'Plantation Drive': {
    nameY: 0.45,
    nameX: 0.50,
    fontSize: 0.055,
    color: '#1a1a1a',
    fontStyle: 'bold'
  },
  'Beach Cleanup': {
    nameY: 0.50,
    nameX: 0.50,
    fontSize: 0.055,
    color: '#1a1a1a',
    fontStyle: 'bold'
  },
  'World Biggest Beach Cleanup': {
    nameY: 0.50,
    nameX: 0.50,
    fontSize: 0.055,
    color: '#1a1a1a',
    fontStyle: 'bold'
  },
  'CPR & FIRST RESPONSE WORKSHOP': {
    nameY: 0.50,
    nameX: 0.50,
    fontSize: 0.055,
    color: '#1a1a1a',
    fontStyle: 'bold'
  },
  'Fire Safety': {
    nameY: 0.50,
    nameX: 0.50,
    fontSize: 0.055,
    color: '#1a1a1a',
    fontStyle: 'bold'
  },
  'Medical Camp': {
    nameY: 0.50,
    nameX: 0.50,
    fontSize: 0.055,
    color: '#1a1a1a',
    fontStyle: 'bold'
  },
  'Park Cleanup & Restoration': {
    nameY: 0.438,
    nameX: 0.50,
    fontSize: 0.055,
    color: '#1a1a1a',
    fontStyle: 'bold'
  },
};

// ── Template-based canvas certificate ────────────────────────────────────────

interface TemplateCanvasProps {
  templatePath: string;
  memberName: string;
  config: typeof TEMPLATE_CONFIGS[string];
  onReady: (canvas: HTMLCanvasElement) => void;
}

const TemplateCanvas: React.FC<TemplateCanvasProps> = ({ templatePath, memberName, config, onReady }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      // Draw template background
      ctx.drawImage(img, 0, 0);

      // Compute font size from image width
      const px = Math.round(img.naturalWidth * config.fontSize);
      ctx.font = `${config.fontStyle} ${px}px Georgia, "Times New Roman", serif`;
      ctx.fillStyle = config.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const x = img.naturalWidth * config.nameX;
      const y = img.naturalHeight * config.nameY;
      ctx.fillText(memberName, x, y);

      onReady(canvas);
    };

    img.onerror = () => {
      // If template image fails, draw a plain fallback
      canvas.width = 800;
      canvas.height = 600;
      ctx.fillStyle = '#f5f5f0';
      ctx.fillRect(0, 0, 800, 600);
      ctx.font = 'bold 44px Georgia, serif';
      ctx.fillStyle = '#1a1a1a';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(memberName, 400, 300);
      onReady(canvas);
    };

    img.src = templatePath;
  }, [templatePath, memberName, config, onReady]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '1rem' }}
    />
  );
};

// ── Main Certificate component ───────────────────────────────────────────────

export const Certificate: React.FC<CertificateProps> = ({
  memberName,
  eventTitle,
  eventDate,
  points,
  memberId,
  profileImage,
  certificateTemplate,
}) => {
  const defaultRef = useRef<HTMLDivElement>(null);
  const [readyCanvas, setReadyCanvas] = useState<HTMLCanvasElement | null>(null);

  const templatePath = certificateTemplate ? `/Certificates/${certificateTemplate}/PNG.png` : null;
  const templateConfig = certificateTemplate ? TEMPLATE_CONFIGS[certificateTemplate] : null;

  const handleDownload = async () => {
    let dataUrl: string;

    if (readyCanvas) {
      // Template certificate — canvas is already rendered
      dataUrl = readyCanvas.toDataURL('image/png');
    } else if (defaultRef.current) {
      // Default HTML certificate — use html2canvas
      const canvas = await html2canvas(defaultRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });
      dataUrl = canvas.toDataURL('image/png');
    } else {
      return;
    }

    const link = document.createElement('a');
    link.download = `certificate-${eventTitle.replace(/\s+/g, '-')}.png`;
    link.href = dataUrl;
    link.click();
  };

  return (
    <div className="space-y-4">
      {/* Certificate preview */}
      <div className="rounded-2xl overflow-hidden shadow-lg">
        {templatePath && templateConfig ? (
          <TemplateCanvas
            templatePath={templatePath}
            memberName={memberName}
            config={templateConfig}
            onReady={setReadyCanvas}
          />
        ) : (
          // Default HTML certificate
          <div
            ref={defaultRef}
            className="relative bg-gradient-to-br from-amber-50 via-white to-blue-50 p-12 border-8 border-double border-amber-400"
            style={{ width: '800px', height: '600px' }}
          >
            <div className="absolute top-4 left-4 w-20 h-20 border-t-4 border-l-4 border-amber-400" />
            <div className="absolute top-4 right-4 w-20 h-20 border-t-4 border-r-4 border-amber-400" />
            <div className="absolute bottom-4 left-4 w-20 h-20 border-b-4 border-l-4 border-amber-400" />
            <div className="absolute bottom-4 right-4 w-20 h-20 border-b-4 border-r-4 border-amber-400" />

            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="mb-6 flex flex-col items-center gap-4">
                <div className="bg-[#0D5128] p-3 rounded-lg">
                  <img src="/website-04.png" alt="Hammad Foundation" className="w-100 h-auto object-contain" />
                </div>
                {profileImage && (
                  <img src={profileImage} alt={memberName} className="w-20 h-20 rounded-full object-cover border-4 border-amber-400" />
                )}
              </div>
              <h1 className="text-5xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'serif' }}>
                Certificate of Participation
              </h1>
              <p className="text-xl text-gray-600 mb-8">Hammad Foundation Volunteer Network</p>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent mb-8" />
              <p className="text-lg text-gray-700 mb-4">This is to certify that</p>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 px-8 py-3 border-b-2 border-amber-400">
                {memberName}
              </h2>
              <p className="text-lg text-gray-700 mb-2">has successfully participated in</p>
              <h3 className="text-2xl font-semibold text-gray-800 mb-6">"{eventTitle}"</h3>
              <div className="flex items-center gap-8 mb-8">
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {new Date(eventDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div className="w-px h-12 bg-gray-300" />
                <div>
                  <p className="text-sm text-gray-600">Points Earned</p>
                  <p className="text-lg font-semibold text-amber-600">{points} Points</p>
                </div>
              </div>
              <p className="text-sm text-gray-500">Member ID: {memberId}</p>
            </div>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-10">
              <p className="text-6xl font-bold text-gray-400">HF</p>
            </div>
          </div>
        )}
      </div>

      {/* Download button */}
      <button onClick={handleDownload} className="btn btn-primary w-full">
        <Download size={20} />
        Download Certificate
      </button>
    </div>
  );
};
